import type { StudioApi } from '../useTechnicalStudio';
import { fmtPrice, SectionHeading } from './ui';

export default function ConfluenceScanner({ api }: { api: StudioApi }) {
  const { confluence, decimals } = api;

  return (
    <section id="confluence" className="scroll-mt-24 rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
      <SectionHeading
        eyebrow="So sánh mức giá"
        title="Confluence Scanner"
        subtitle="Tự động so sánh các mức Pivot và Fibonacci để tìm những vùng giá nằm gần nhau. Đây là phép so sánh mức giá, không phải tín hiệu giao dịch."
      />

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Pivot levels */}
        <div className="rounded-2xl border border-slate-200/80 bg-slate-50/50 p-5">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
              <i className="ri-crosshair-2-line" />
            </span>
            <h3 className="text-sm font-bold text-slate-800">Pivot Levels</h3>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {api.pivotLevels.levels.map((l) => (
              <span key={l.key} className="rounded-lg bg-white px-3 py-1.5 font-mono text-xs font-semibold text-slate-700 ring-1 ring-slate-200">
                {l.label} <span className="text-emerald-600">{fmtPrice(l.price, decimals)}</span>
              </span>
            ))}
          </div>
        </div>

        {/* Fibonacci levels */}
        <div className="rounded-2xl border border-slate-200/80 bg-slate-50/50 p-5">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-50 text-violet-600">
              <i className="ri-route-line" />
            </span>
            <h3 className="text-sm font-bold text-slate-800">Fibonacci Levels</h3>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {api.fibLevels.map((l) => (
              <span key={l.ratio} className="rounded-lg bg-white px-3 py-1.5 font-mono text-xs font-semibold text-slate-700 ring-1 ring-slate-200">
                {l.ratio} <span className="text-violet-600">{fmtPrice(l.price, decimals)}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Confluence hits */}
      <div className="mt-6">
        <h3 className="font-display text-base font-bold text-slate-900">Mức giá trùng lặp</h3>
        <p className="mt-1 text-xs text-slate-400">Các mức Pivot và Fibonacci nằm cách nhau ≤ 5 pip.</p>

        {confluence.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 px-6 py-10 text-center">
            <i className="ri-search-eye-line text-3xl text-slate-300" />
            <p className="mt-3 text-sm text-slate-400">Không có mức giá trùng lặp trong khoảng 5 pip.</p>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {confluence.map((c, i) => (
              <div key={i} className="flex flex-col gap-2 rounded-2xl border border-violet-200/70 bg-violet-50/40 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-900">
                    Mức giá trùng lặp <span className="font-mono text-violet-700">{fmtPrice(c.fibPrice, decimals)} – {fmtPrice(c.pivotPrice, decimals)}</span>
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Pivot {c.pivotKey} và Fibonacci {c.fibRatio} đang nằm gần nhau.
                  </p>
                </div>
                <span className="shrink-0 rounded-full bg-violet-600 px-3 py-1 font-mono text-xs font-bold text-white">
                  {c.distancePips.toFixed(0)} pip
                </span>
              </div>
            ))}
          </div>
        )}

        <p className="mt-4 flex items-center gap-1.5 text-[11px] text-slate-400">
          <i className="ri-information-line" />
          Đây là phép so sánh mức giá (Price Level Comparison), không phải khuyến nghị mua hay bán.
        </p>
      </div>
    </section>
  );
}